import {
  credentialMode,
  isMunicipalCredential,
  credentialBrandName,
  credentialOrganizationName,
} from "@/services/config";

/**
 * @module services/credential
 * @description UC-REG-295 — Credencial municipal del portal del paciente
 *   (tenants con `VUE_APP_CREDENTIAL_MODE=municipal`, ej. Campana). A
 *   diferencia de la credencial de afiliado (Member + token TOTP), acá la
 *   tarjeta se arma desde el User logueado (nombre, apellido, DNI) y el grupo
 *   familiar sale de `GET /family-group/me`. Sin número de afiliado, sin plan,
 *   sin token. Espejo de `services/credentials.js` de la app móvil.
 */

/** @type {Object<string, string>} Parentescos del FamilyGroup (enum de la API) → etiqueta. */
export const FAMILY_MEMBER_TYPE_LABELS = {
  holder: "Titular",
  spouse: "Cónyuge / pareja",
  child: "Hijo/a",
  "adult child": "Hijo/a mayor de edad",
  "dependent family member": "Familiar a cargo",
};

/** @type {Array<{value: string, label: string}>} Opciones que puede elegir el titular. */
export const FAMILY_MEMBER_TYPE_OPTIONS = [
  "child",
  "adult child",
  "spouse",
  "dependent family member",
].map((value) => ({ value, label: FAMILY_MEMBER_TYPE_LABELS[value] }));

/** @type {Object<string, string>} Códigos de error de `/family-group/me` → mensaje para el vecino. */
export const FAMILY_GROUP_ERROR_MESSAGES = {
  MissingRequiredFields:
    "Completá nombre, apellido, DNI, fecha de nacimiento y parentesco.",
  InvalidDni: "El DNI tiene que tener 7 u 8 números.",
  InvalidBirthDate: "La fecha de nacimiento no es válida.",
  InvalidGender: "El sexo indicado no es válido.",
  InvalidMemberType: "Elegí un parentesco válido.",
  CannotAddSelf: "Ese es tu propio DNI: vos ya sos el titular del grupo.",
  MemberAlreadyInGroup: "Esa persona ya forma parte de un grupo familiar.",
  MemberHasOwnAccount:
    "Esa persona ya tiene su propia cuenta en Salud Campana: sus datos los administra desde su usuario.",
  NotHolder: "Solo el titular del grupo puede hacer cambios.",
  MemberNotFound: "No encontramos a ese integrante en tu grupo familiar.",
  CannotEditHolder: "El titular no se edita desde acá: usá tu perfil.",
  MemberCreationFailed: "No pudimos agregar al integrante. Probá de nuevo.",
  PatientsOnly: "El grupo familiar es solo para pacientes.",
  // UC-REG-312 — verificación del integrante en el padrón. Los códigos con
  // `null` los muestra el form inline (sin toast).
  IdentityMismatch: null,
  PersonDeceased: null,
  MissingIdentityFields:
    "Completá DNI, fecha de nacimiento y sexo del integrante.",
  TooManyLookups: "Hiciste demasiadas búsquedas. Esperá unos minutos.",
  PadronUnavailable:
    "El padrón del Municipio no está disponible en este momento. Intentá más tarde.",
  PadronIncomplete:
    "El padrón no devolvió todos los datos en este momento. Intentá más tarde.",
  MemberDataVerified:
    "Los datos de ese integrante están verificados en el padrón y no se editan desde acá.",
  LookupNotAvailable: "La búsqueda en el padrón no está disponible.",
};

/**
 * @description Capitaliza cada palabra (los nombres se guardan en minúsculas).
 * @param {string} value
 * @returns {string}
 */
export function titleCase(value) {
  return String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * @description Formatea un DNI agrupando de a miles con puntos (30.111.222).
 * @param {string|number|null} dni
 * @returns {string} "—" si no hay DNI
 */
export function formatDni(dni) {
  if (!dni) return "—";
  const clean = String(dni).replace(/[^0-9]/g, "");
  if (!clean) return "—";
  return clean.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/**
 * @description Normaliza un User (titular logueado o integrante del grupo) al
 *   shape que renderiza `MunicipalCredentialCard`.
 * @param {Object} user - User con firstName, lastName y governmentId.number (o dni)
 * @returns {{_id: string, firstName: string, lastName: string, fullName: string, dni: string|null}|null}
 */
export function toMunicipalCredential(user) {
  if (!user || !user._id) return null;
  const rawDni =
    (user.governmentId && user.governmentId.number) || user.dni || null;
  const firstName = titleCase(user.firstName);
  const lastName = titleCase(user.lastName);
  return {
    _id: user._id,
    firstName,
    lastName,
    // El Municipio pide Nombre y Apellido EN ESE ORDEN (no "Apellido, Nombre").
    fullName: `${firstName} ${lastName}`.trim(),
    dni: rawDni ? String(rawDni).replace(/[^0-9]/g, "") : null,
  };
}

/**
 * @description Etiqueta de un parentesco del grupo familiar.
 * @param {string} type - valor del enum de la API
 * @returns {string}
 */
export function familyMemberTypeLabel(type) {
  return FAMILY_MEMBER_TYPE_LABELS[type] || type || "—";
}

/**
 * @description Mensaje para el usuario a partir de un error de axios de
 *   `/family-group/me`. `undefined` si no hay código conocido (la capa HTTP
 *   usa entonces su mensaje genérico; `null` lo silenciaría).
 * @param {Object} err - error de axios
 * @returns {string|null}
 */
export function familyGroupErrorMessage(err) {
  const code =
    err && err.response && err.response.data && err.response.data.error;
  if (
    code &&
    Object.prototype.hasOwnProperty.call(FAMILY_GROUP_ERROR_MESSAGES, code)
  ) {
    return FAMILY_GROUP_ERROR_MESSAGES[code];
  }
  return undefined;
}

/**
 * @description Textos de marca de la credencial municipal del tenant.
 * @returns {{brandName: string, organizationName: string}}
 */
export function getMunicipalCredentialBranding() {
  return {
    brandName: credentialBrandName,
    organizationName: credentialOrganizationName,
  };
}

export { credentialMode, isMunicipalCredential };
